// ProjectParticipants — manage the partner companies on a project
// (owner-side management; read-only for everyone else, enforced by the
// backend with 403s which we surface here).
//
// Data:
//   GET    /projects/:projectId/participants
//   GET    /projects/:projectId/participants/roster
//   GET    /projects/:projectId/participants/company-search?q=
//   POST   /projects/:projectId/participants
//   PATCH  /projects/:projectId/participants/:pid
//   DELETE /projects/:projectId/participants/:pid
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Table, Button, Select, Tag, Modal, message, Empty, Input, Switch, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, ScheduleOutlined, SwapOutlined, DollarOutlined, LinkOutlined, MailOutlined, AppstoreOutlined, KeyOutlined } from "@ant-design/icons";
import CoordinationAccessGrid from "components/Partners/CoordinationAccessGrid";
import PartnerPersonModal from "components/Partners/PartnerPersonModal";
import PartnerAccessDialog from "pages/Partners/PartnerAccessDialog";
import ParticipantRatesModal from "components/Coordination/ParticipantRatesModal";
import OwnerTeamCard from "components/Coordination/OwnerTeamCard";
import OversightPanel from "components/Coordination/OversightPanel";
import { PartnerMissingBanner, PartnerProfileSummary, PartnerProfileEditor, PartnerEmailsDialog } from "components/Coordination/PartnerProfile";
import { PartnerImportButton, PartnerImportStatus } from "components/Coordination/PartnerImport";
import PartnerCorePicker from "components/Coordination/PartnerCorePicker";
import { getIssueExportCost, setIssueExportCost } from "api/projects/issueExportCost";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useProjectAccess from "hooks/useProjectAccess";
import { rememberProject } from "helpers/projectSelection";
import { getDiscipline } from "api/discipline/getDiscipline";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import {
  getParticipants,
  getParticipantsRoster,
  getPartnerOrgs,
  addParticipant,
  updateParticipant,
  moveParticipantUser,
  removeParticipant,
  addCrewMember,
  getCopySources,
  copyParticipants,
  inviteParticipants,
  getRecipientRoster,
  setPartnerJobTitle,
} from "api/participants/participantsApi";
import { matchOption } from "components/SearchSelect/searchSelect";

const OWNER_ONLY_MSG = "Only the project owner can manage participants";

const ROLE_OPTIONS = [
  { value: "consultant", label: "Consultant" },
  { value: "contractor", label: "Contractor" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "supplier", label: "Supplier" },
];

// What the state means, in the words somebody would use about it. "No
// account yet" is not a fault to fix - it is what the join link in the
// approved deliverable is for - so it is grey, while a person whose own
// firm is already on this project but who is missing from its roster is
// amber: every per-company report and every responsible dropdown is
// quietly skipping them.
const UNPLACED_TAG = {
  "no-account": { color: "", label: "no account yet" },
  "no-company": { color: "blue", label: "no company" },
  "off-roster": { color: "orange", label: "missing from a roster here" },
  "other-company": { color: "purple", label: "another company" },
};

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

// Cheap project-title lookup — same pattern as ApprovalQueue.jsx
async function fetchProjects() {
  const res = await fetch(`${url}/projects/picker`, { headers: auth() });
  if (!res.ok) return [];
  const body = await res.json();
  return Array.isArray(body) ? body : (body?.projects || []);
}

// A partner's trades as NAMES (owner, 2026-10-16: trades shown under every
// partner). `trades` are Discipline keys; a key the list does not know (a
// typed trade) reads as itself.
function tradeNames(trades, tradeOptions) {
  return (trades || []).map((t) => {
    const hit = (tradeOptions || []).find((o) => String(o.value).toLowerCase() === String(t).toLowerCase());
    return hit ? hit.name : String(t);
  });
}

// `embedded` (owner, 2026-10-16): rendered inside the one Partners view
// (pages/Partners/PartnersHub), which carries the breadcrumb and the title.
export default function ProjectParticipantsPage({ projectId: projectIdProp = "", embedded = false } = {}) {
  const params = useParams();
  const projectId = projectIdProp || params.projectId;
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user?.userInfo);
  // Arriving here (deep link, breadcrumb, landing) selects this project for
  // the whole coordination suite - remember it.
  useEffect(() => { if (projectId) rememberProject(userInfoRedux?._id || userInfoRedux?.id, projectId); }, [projectId]); // eslint-disable-line

  const [projectTitle, setProjectTitle] = useState("");
  const [participants, setParticipants] = useState([]);
  // Job titles (owner brief 2026-10-11 T44): the list comes with the owner
  // team card's read, so the partner rows below offer the same one.
  const [jobTitles, setJobTitles] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  // THE SECOND LIST. `null` means there is nothing to show - a partner
  // company gets a 403 here, which is not an error to report at them.
  // `unplacedError` is separate ON PURPOSE: a 500 and "you are not the
  // owner company" both arrive as a failed fetch, and collapsing them
  // would hide a broken endpoint behind a section that simply is not
  // there - which looks exactly like a project where everybody is placed.
  const [unplaced, setUnplaced] = useState(null);
  const [unplacedError, setUnplacedError] = useState("");

  // DECLARED ABOVE THEIR CALLERS. Both of these were read by handlers
  // defined earlier in the file - safe at runtime, because a handler runs
  // long after the component body - but it is the const-above-declaration
  // shape that has white-paged the owner three times, and since
  // 2026-09-20 no-use-before-define is an ERROR rather than a warning.
  // Pre-existing, not introduced by the executive work; reordered here
  // rather than exempted, because "this particular one is safe" is how
  // the next one gets written.
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getParticipants(projectId),
      getParticipantsRoster(projectId).catch(() => []),
      getRecipientRoster(projectId).then(
        (res) => ({ ok: res }),
        (e) => ({ err: e }),
      ),
    ])
      .then(([rows, rosterRes, unplacedRes]) => {
        setParticipants(Array.isArray(rows) ? rows : rows?.participants || []);
        setRoster(Array.isArray(rosterRes) ? rosterRes : []);
        if (unplacedRes.ok) {
          setUnplaced(unplacedRes.ok);
          setUnplacedError("");
        } else if (unplacedRes.err?.status === 403) {
          setUnplaced(null);
          setUnplacedError("");
        } else {
          setUnplaced(null);
          setUnplacedError(unplacedRes.err?.message || "Could not work out who is in no company");
        }
      })
      .catch((e) => message.error(e.message || "Failed to load participants"))
      .finally(() => setLoading(false));
  }, [projectId]);

  // CRUD rights from the server (GET /projects/:id/access): owner-company
  // side with participantAdd / participantEdit / participantDelete (or the
  // legacy participantManage). Partner companies see this page read-only.
  const { can, canEditPlan } = useProjectAccess(projectId);
  const canAdd = !!can.participantAdd;
  const canEdit = !!can.participantEdit;
  // Moving a person BETWEEN contractors: admins, company admins and the
  // PROJECT OWNER. It re-files an employee's account - the thing every
  // responsible dropdown groups by - so an ordinary participantEdit holder
  // does not get it. The server decides and says so on every list row
  // (`canMovePeople`); reading it from there rather than guessing from
  // redux flags means the button and the endpoint can never disagree.
  const canMovePeople = !!(userInfoRedux?.admin || userInfoRedux?.companyAdmin
    || (participants || []).some((r) => r.canMovePeople));

  // ── move a person to another contractor ─────────────────────────────
  const [moveRow, setMoveRow] = useState(null);      // the participant they are on
  const [movePerson, setMovePerson] = useState(null);
  const [moveTo, setMoveTo] = useState(undefined);
  const [moveAssignments, setMoveAssignments] = useState(true);
  const [moveWorking, setMoveWorking] = useState(false);
  const openMoveUser = (row, u) => {
    setMoveRow(row); setMovePerson(u); setMoveTo(undefined); setMoveAssignments(true);
  };
  const submitMoveUser = async () => {
    if (!moveTo) return message.warning("Pick the contractor to move them to");
    setMoveWorking(true);
    try {
      const r = await moveParticipantUser(projectId, moveRow._id, {
        userId: movePerson?.userId || undefined,
        email: movePerson?.email,
        toCompanyId: moveTo,
        moveAssignments,
      });
      const bits = [];
      if (r?.movedAccount) bits.push("account re-filed");
      if (r?.movedRosterEntry) bits.push("roster entry moved");
      if (r?.movedSupervisor) bits.push("supervisor moved");
      const n = (r?.checklistItems || 0) + (r?.issues || 0) + (r?.planItems || 0);
      if (n) bits.push(`${n} assignment${n === 1 ? "" : "s"} followed`);
      message.success(`${movePerson?.name || "Person"} moved${bits.length ? ` - ${bits.join(", ")}` : ""}`);
      // What could NOT be moved is said out loud rather than left to be
      // discovered later on a row filed under the wrong contractor.
      if (r?.skipped?.length) {
        Modal.info({
          title: `${r.skipped.length} thing${r.skipped.length === 1 ? "" : "s"} left where they were`,
          content: (
            <div style={{ maxHeight: 260, overflow: "auto" }}>
              {r.skipped.map((sk, i) => <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>{sk}</div>)}
            </div>
          ),
        });
      }
      setMoveRow(null); setMovePerson(null);
      load();
    } catch (e) { message.error(e.message || "Move failed"); }
    finally { setMoveWorking(false); }
  };
  const canDelete = !!can.participantDelete;
  const canManage = canAdd || canEdit || canDelete;
  // Kept for the server-403 fallback path (handleOwnerOnly).
  const [, setCanManage] = useState(true);

  // Bumped by an upload so the import status list re-reads at once.
  const [importTick, setImportTick] = useState(0);
  // Add-partner modal state
  const [addOpen, setAddOpen] = useState(false);
  // COPY PARTNERS FROM ANOTHER PROJECT (owner, 2026-09-21).
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySources, setCopySources] = useState([]);
  const [copyFrom, setCopyFrom] = useState(null);
  const [copyRows, setCopyRows] = useState([]);
  const [copyPicked, setCopyPicked] = useState([]);
  const [copyBusy, setCopyBusy] = useState(false);

  const handleOwnerOnly = () => {
    setCanManage(false);
    setAddOpen(false);
    message.error(OWNER_ONLY_MSG);
  };

  const openCopy = async () => {
    setCopyOpen(true); setCopyFrom(null); setCopyRows([]); setCopyPicked([]);
    try {
      const r = await getCopySources(projectId);
      setCopySources(r?.projects || []);
    } catch (e) { message.error(e.message || "Could not list projects"); }
  };

  // The partners of the chosen source, read through the NORMAL list route -
  // which is what strips rates from anyone who may not see them. Reading
  // them here rather than trusting the picker means the permission is
  // enforced on the read side, not only on the write.
  const chooseCopySource = async (id) => {
    setCopyFrom(id); setCopyRows([]); setCopyPicked([]);
    try {
      const rows = await getParticipants(id);
      setCopyRows(Array.isArray(rows) ? rows : rows?.participants || []);
    } catch (e) { message.error(e.message || "Could not read that project's partners"); }
  };

  const runCopy = async () => {
    setCopyBusy(true);
    try {
      const r = await copyParticipants(projectId, copyFrom, copyPicked);
      const n = (r?.copied || []).length;
      // A partner already here is the SAME partner: not copied again, but
      // its people are synced from the other project (added and renamed,
      // nobody removed) - said as such, not as "skipped".
      const synced = (r?.skipped || []).filter((x) => x.why === "already on this project");
      const skipped = (r?.skipped || []).length - synced.length;
      const added = synced.reduce((t, x) => t + ((x.synced && x.synced.added) || 0), 0);
      message.success(
        `${n} partner${n === 1 ? "" : "s"} copied`
        + (synced.length ? `, ${synced.length} already here - people synced${added ? ` (+${added})` : ""}` : "")
        + (skipped ? `, ${skipped} skipped` : "")
      );
      // Admin-section permissions travel only when a company admin copies;
      // anything left off is SAID, per partner, rather than quietly lost.
      const leftOff = (r?.copied || []).filter((c) => (c.notCarried || []).length);
      if (leftOff.length) {
        message.warning(
          `Not carried over - only a company admin can grant these: ${leftOff.map((c) => `${c.companyName} (${c.notCarried.join(", ")})`).join("; ")}.`,
          10
        );
      }
      setCopyOpen(false);
      load();
    } catch (e) {
      if (e.status === 403 || e.status === 404) handleOwnerOnly();
      else message.error(e.message || "Copy failed");
    } finally { setCopyBusy(false); }
  };

  const [addCompanyId, setAddCompanyId] = useState(undefined);
  const [addRole, setAddRole] = useState(undefined);
  const [addTrades, setAddTrades] = useState([]);
  // Declared contact users — name/email/role all required per row.
  const emptyContact = () => ({ key: Math.random().toString(36).slice(2), name: "", email: "", role: "" });
  const [addUsers, setAddUsers] = useState([emptyContact()]);
  const setContact = (setter) => (key, patch) =>
    setter((us) => us.map((u) => (u.key === key ? { ...u, ...patch } : u)));
  const validateContacts = (users) => {
    const filled = users.filter((u) => u.name.trim() || u.email.trim() || u.role.trim());
    if (!filled.length) return { err: "Add at least one contact user (name, email, role)" };
    for (const u of filled) {
      if (!u.name.trim() || !u.role.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u.email.trim())) {
        return { err: "Every contact needs a name, a valid email and a role" };
      }
    }
    return { users: filled.map((u) => ({ name: u.name.trim(), email: u.email.trim(), role: u.role.trim() })) };
  };
  const [addWorking, setAddWorking] = useState(false);
  // YOUR PARTNERS: the partner orgs this company already works with. Picking
  // one brings its people (owner, 2026-09-21: "reference the partner org").
  const [partnerOrgs, setPartnerOrgs] = useState({ loading: false, orgs: [] });
  useEffect(() => {
    if (!addOpen || !projectId) return;
    setPartnerOrgs((p) => ({ ...p, loading: true }));
    getPartnerOrgs(projectId)
      .then((r) => setPartnerOrgs({ loading: false, orgs: (r && r.orgs) || [] }))
      .catch(() => setPartnerOrgs({ loading: false, orgs: [] }));
  }, [addOpen, projectId]);
  const pickedOrg = partnerOrgs.orgs.find((o) => o.companyId === addCompanyId) || null;
  // A NEW PARTNER COMPANY by name. There is no search of other companies:
  // the firm is identified by the first contact's email on the server
  // (routes/participants partnerCompanyForNew), so Add partner never lists
  // another customer's name (2026-09-21).
  const [newCompanyName, setNewCompanyName] = useState("");

  // Edit modal state
  const [editRow, setEditRow] = useState(null);
  const [editRole, setEditRole] = useState(undefined);
  const [editTrades, setEditTrades] = useState([]);
  // Trade options = the company's disciplines (Document Control list,
  // seeded with defaults for every company). Custom entries still allowed.
  const [tradeOptions, setTradeOptions] = useState([]);
  useEffect(() => {
    const company = userInfoRedux?.company;
    if (!company) return;
    getDiscipline(company)
      .then((list) => {
        const rows = Array.isArray(list) ? list : [];
        setTradeOptions(rows.filter((d) => d && d.key && d.key !== "Issues").map((d) => ({ value: d.key, name: d.value || d.key, label: d.value && d.value !== d.key ? `${d.key} (${d.value})` : d.key })));
      })
      .catch(() => setTradeOptions([]));
  }, [userInfoRedux?.company]);
  const [editWorking, setEditWorking] = useState(false);

  // ADD / EDIT ONE PERSON ON A PARTNER ROW - the one dialog the Partners view
  // uses in both scopes (components/Partners/PartnerPersonModal; owner,
  // 2026-10-16: merge the add-person paths). Supervisors get the weekly
  // project-wide report; executives (a flag on the supervisor entry) only the
  // weekly summary; a person (users[], with a title) is assignable to issues
  // and plan items. Cost view is showCost on the supervisor entry.
  // { row, list: "users" | "supervisors" | null (adding), idx, initial }
  const [personEdit, setPersonEdit] = useState(null);
  const [personWorking, setPersonWorking] = useState(false);
  // ---- delegated crew (partner side) -------------------------------
  // Partner supervisors normally cannot touch the roster: the account is
  // sponsored by the owner. When the owner flips "crew self-serve" for a
  // company, that company's supervisors get this button on their own row.
  const [crewRow, setCrewRow] = useState(null);
  const [crewName, setCrewName] = useState("");
  const [crewEmail, setCrewEmail] = useState("");
  const [crewRole, setCrewRole] = useState("");
  const [crewWorking, setCrewWorking] = useState(false);
  const openAddCrew = (row) => { setCrewRow(row); setCrewName(""); setCrewEmail(""); setCrewRole(""); };
  const handleCrewAdd = async () => {
    if (!crewRow) return;
    if (!crewName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(crewEmail.trim())) {
      message.warning("Crew member needs a name and a valid email");
      return;
    }
    setCrewWorking(true);
    try {
      await addCrewMember(projectId, crewRow._id, {
        name: crewName.trim(),
        email: crewEmail.trim().toLowerCase(),
        role: crewRole.trim() || "Crew",
      });
      message.success("Crew member added - they can sign in from their issue link");
      setCrewRow(null);
      load();
    } catch (e) {
      message.error(e.message || "Failed to add crew member");
    } finally {
      setCrewWorking(false);
    }
  };
  const toggleCrewInvite = async (row, on) => {
    try {
      await updateParticipant(projectId, row._id, { allowSupervisorInvite: !!on });
      message.success(on
        ? `${row.companyName} supervisors can now add their own crew`
        : `${row.companyName} roster is owner-managed again`);
      load();
    } catch (e) {
      if (e.status === 403) handleOwnerOnly();
      else message.error(e.message || "Failed to update");
    }
  };

  // `prefill` is how somebody from the "in no company" list below is
  // placed: the same dialog, the same write path, the same validation -
  // name and address already filled in, so the only thing left to say is
  // what they do here. Nothing new writes to a roster.
  const openAddPerson = (row, prefill = null, kind = "person") => {
    setPersonEdit({ row, list: null, idx: null, initial: { name: prefill?.name || "", email: prefill?.email || "", kind, title: "", showCost: false } });
  };
  const openAddEmployee = (row, prefill = null) => openAddPerson(row, prefill, "person");
  // A personal link that signs this roster person in to the project - to
  // paste into a text message, or to email again. Name and email are all
  // they need (back/routes/participants POST /:pid/sign-in-link).
  // THE ONE ACCESS DIALOG (pages/Partners/PartnerAccessDialog), scoped to
  // this project's roster row: whoever may edit this roster may set it.
  const [access, setAccess] = useState(null); // { row, person }
  const openAccess = (row, person) => setAccess({ row, person });

  const signInLink = async (row, person, send) => {
    try {
      const r = await fetch(`${url}/projects/${projectId}/participants/${row._id}/sign-in-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
        body: JSON.stringify({ email: person.email, send: !!send }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      if (send) {
        message.success(`Sign-in link emailed to ${person.email}`);
      } else {
        try { await navigator.clipboard.writeText(b.link); message.success(`Sign-in link for ${person.name || person.email} copied - it works for 30 days`); }
        catch (e) { Modal.info({ title: `Sign-in link for ${person.name || person.email}`, content: <Input.TextArea value={b.link} autoSize readOnly /> }); }
      }
    } catch (e) {
      message.error(e.message || "Could not create the link");
    }
  };
  // SIGN-IN LINK AND ACCESS, IN FRONT OF EVERY PERSON (owner, 2026-10-16:
  // "a button infront of each user on all partners to send them a signin
  // link ... make access for each user/partner users editable"). The link
  // brings the standard partner access with it (server: utils/
  // partnerAccessKeys); Access edits this person's own grants.
  const linkButtons = (row, person) => (
    <>
      <Button size="small" icon={<MailOutlined />} style={{ marginLeft: 6 }}
              title="Email them a link that signs them in - no password needed"
              onClick={() => signInLink(row, person, true)}>Send sign-in link</Button>
      <Button type="text" size="small" icon={<LinkOutlined />} title="Copy a link that signs them in (no password needed)" onClick={() => signInLink(row, person, false)} />
      <Button size="small" icon={<KeyOutlined />} title="What this person may do" onClick={() => openAccess(row, person)}>Access</Button>
    </>
  );

  const openEditEmployee = (row, idx) => {
    const u = (row.users || [])[idx] || {};
    setPersonEdit({ row, list: "users", idx, initial: { name: u.name || "", email: u.email || "", kind: "person", title: u.role || "", showCost: false } });
  };
  // Both lists persist as a full-array replace on the participant row. A
  // kind change between a person and a supervisor/executive MOVES the entry:
  // out of one list, into the other, in one write.
  const savePerson = async (v) => {
    if (!personEdit) return;
    const { row, list, idx } = personEdit;
    // CARRY isExecutive FOR EVERY SUPERVISOR, NOT ONLY THE ONE BEING EDITED.
    // This rebuilds the whole array and sends it back, so any field not
    // named here is dropped - editing ONE supervisor's name would silently
    // demote every executive on the row. The server carries it too
    // (utils/executiveContacts.keepExecutive).
    const lists = {
      users: (row.users || []).map((u) => ({ name: u.name, email: u.email, role: u.role })),
      supervisors: (row.supervisors || []).map((x) => ({ name: x.name, email: x.email, isExecutive: !!x.isExecutive })),
    };
    const target = v.kind === "person" ? "users" : "supervisors";
    const entry = target === "users"
      ? { name: v.name, email: v.email, role: v.title }
      : { name: v.name, email: v.email, isExecutive: v.kind === "executive" };
    // Sent only by someone allowed to change it; silence means "unchanged"
    // on the server, so a roster editor without the right still saves names.
    if (target === "supervisors" && row.canSetCostView) entry.showCost = !!v.showCost;
    const body = {};
    if (list && list === target) {
      lists[target] = lists[target].map((x, i) => (i === idx ? entry : x));
      body[target] = lists[target];
    } else {
      if (list) { lists[list] = lists[list].filter((_, i) => i !== idx); body[list] = lists[list]; }
      lists[target] = [...lists[target], entry];
      body[target] = lists[target];
    }
    setPersonWorking(true);
    try {
      await updateParticipant(projectId, row._id, body);
      const what = v.kind === "person" ? "Employee" : (v.kind === "executive" ? "Executive" : "Supervisor");
      message.success(list ? `${what} updated` : `${what} added`);
      setPersonEdit(null);
      load();
    } catch (e) {
      if (e.status === 403) handleOwnerOnly();
      else message.error(e.message || "Could not save the person");
    } finally {
      setPersonWorking(false);
    }
  };
  const handleEmployeeRemove = async (row, idx) => {
    try {
      await updateParticipant(projectId, row._id, {
        users: (row.users || []).filter((_, i) => i !== idx).map((u) => ({ name: u.name, email: u.email, role: u.role })),
      });
      message.success("Employee removed");
      load();
    } catch (e) {
      if (e.status === 403) handleOwnerOnly();
      else message.error(e.message || "Failed to remove employee");
    }
  };

  // The partner whose hourly rates are being edited (owner / admins only).
  const [ratesRow, setRatesRow] = useState(null);
  // COORDINATION ACCESS per partner row (owner, 2026-10-15: "i don't see how
  // to enable the coordination tabs for them"): which tabs this partner sees
  // on this project and what they may do there. The row carries the grid
  // (coordAccess) and whether this person may change it (canSetCoordGrants).
  const [coordRow, setCoordRow] = useState(null);
  // The partner whose profile is being filled in (components/Coordination/PartnerProfile).
  const [profileRow, setProfileRow] = useState(null);
  const [emailsOpen, setEmailsOpen] = useState(false);
  // "Add from <partner>'s people" (owner, 2026-10-15): the row whose picker is open.
  const [coreRow, setCoreRow] = useState(null);

  // WHETHER AN EXPORTED ISSUE REGISTER CARRIES COST, per project (owner,
  // 2026-10-06). It sits on this page because this is where the rates that
  // produce the cost are set - one page for the money on a project.
  // `mayChange` is the server's answer, not ours: only a company
  // administrator may set it, and only they see the control at all.
  const [exportCost, setExportCost] = useState(null);
  const [exportCostSaving, setExportCostSaving] = useState(false);
  // ONLY ASKED FOR BY SOMEBODY WHO COULD CHANGE IT. The setting is for
  // administrators, so a member's or a partner's page load has no reason to
  // fetch it - and this page is opened by every partner on the job. The
  // server decides for real (it answers mayChange and refuses the write);
  // this only decides whether to ask at all.
  const mayAdminister = !!(userInfoRedux?.admin || userInfoRedux?.companyAdmin);
  useEffect(() => {
    if (!mayAdminister) { setExportCost(null); return undefined; }
    let dead = false;
    getIssueExportCost(projectId)
      .then((r) => { if (!dead) setExportCost(r); })
      // A read that fails leaves the control hidden rather than guessing a
      // value: showing "off" because a request failed would invite somebody
      // to "fix" a setting that was never off.
      .catch(() => { if (!dead) setExportCost(null); });
    return () => { dead = true; };
  }, [projectId, mayAdminister]);
  const toggleExportCost = async (next) => {
    setExportCostSaving(true);
    try {
      await setIssueExportCost(projectId, next);
      setExportCost((c) => ({ ...(c || {}), include: next }));
      message.success(next
        ? "Issue exports on this project will include the cost of hours spent"
        : "Issue exports on this project will show hours without cost");
    } catch (e) {
      message.error(e?.message || "Could not change that setting");
    } finally {
      setExportCostSaving(false);
    }
  };
  useEffect(() => {
    load();
    fetchProjects().then((projects) => {
      const p = projects.find((x) => x._id === projectId);
      if (p) setProjectTitle(p.title);
    });
  }, [projectId, load]);

  const companyName = (row) => {
    if (row.companyId && typeof row.companyId === "object") {
      return row.companyId.name || row.companyId.title || "-";
    }
    const id = String(row.companyId || "");
    const match = roster.find((c) => String(c.companyId) === id);
    return match?.companyName || row.companyName || "-";
  };

  // INVITE THE PEOPLE WHO HAVE NO ACCOUNT YET.
  //
  // Offered by ACCOUNT, not by the `invited` flag: that flag records an
  // email about a PARTICULAR project, so on a copied row it can claim one
  // this project never sent. Someone with no account has never registered
  // whichever project invited them - and re-inviting somebody who ignored
  // an older invitation is visible and recoverable, where offering nobody
  // is silent.
  const inviteUninvited = (row) => {
    // A person listed by name only has nobody to invite yet.
    const pending = (row.users || []).filter((u) => !u.userId && u.email);
    if (!pending.length) {
      message.info(`Everyone from ${companyName(row)} already has an account`);
      return;
    }
    Modal.confirm({
      title: `Invite ${pending.length} ${pending.length === 1 ? "person" : "people"} from ${companyName(row)}?`,
      // NAMES THEM, not just a number. He is about to email real people
      // and should see who before it happens, not afterwards in a report.
      content: (
        <div style={{ maxHeight: 220, overflow: "auto", fontSize: 12 }}>
          <div style={{ color: "#888", marginBottom: 6 }}>
            They will each get a registration link for this project. Anyone who
            already has an account is not listed and will not be emailed.
          </div>
          {pending.map((u) => (
            <div key={u.email}>{u.name} <span style={{ color: "#888" }}>- {u.email}</span></div>
          ))}
        </div>
      ),
      okText: `Send ${pending.length}`,
      onOk: async () => {
        try {
          const r = await inviteParticipants(projectId, row._id, pending.map((u) => u.email));
          const n = (r?.sent || []).length;
          const failed = (r?.skipped || []).filter((x) => /could not be sent/i.test(x.why || ""));
          // A FAILED SEND IS NEVER COUNTED AS SENT: somebody who believes
          // an invite went out does not chase it.
          if (failed.length) {
            message.warning(`${n} invited; ${failed.length} could not be emailed`);
          } else {
            message.success(`${n} ${n === 1 ? "invitation" : "invitations"} sent`);
          }
          load();
        } catch (e) {
          if (e.status === 403 || e.status === 404) handleOwnerOnly();
          else message.error(e.message || "Invite failed");
        }
      },
    });
  };

  const handleAdd = async () => {
    const newName = pickedOrg ? "" : newCompanyName.trim();
    if (!addCompanyId && !newName) {
      message.warning("Pick one of your partners, or type a new company's name");
      return;
    }
    // A known partner needs nobody typed - its people are picked from the
    // ones it has on other projects right after. Any contact typed anyway
    // must still be complete.
    const anyTyped = addUsers.some((u) => u.name.trim() || u.email.trim() || u.role.trim());
    const contacts = pickedOrg && pickedOrg.people && !anyTyped ? { users: [] } : validateContacts(addUsers);
    if (contacts.err) {
      message.warning(contacts.err);
      return;
    }
    setAddWorking(true);
    try {
      const created = await addParticipant(projectId, {
        ...(pickedOrg ? { companyId: addCompanyId } : { newCompany: { name: newName } }),
        role: addRole,
        trades: addTrades.length > 0 ? addTrades : undefined,
        users: contacts.users,
      });
      // Projects are specific (owner, 2026-10-15): a known partner arrives
      // with the people typed - possibly nobody - and its people on other
      // projects are offered to pick from straight away.
      message.success(pickedOrg && pickedOrg.people && !contacts.users.length
        ? `${pickedOrg.name} added - pick which of its people work on this project`
        : "Partner company added");
      if (created && created._id && created.core) setCoreRow(created);
      setAddOpen(false);
      setAddCompanyId(undefined);
      setAddRole(undefined);
      setAddTrades([]);
      setAddUsers([emptyContact()]);
      setNewCompanyName("");
      load();
    } catch (e) {
      if (e.status === 403) handleOwnerOnly();
      else message.error(e.message || "Failed to add participant");
    } finally {
      setAddWorking(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditRole(row.role || undefined);
    setEditTrades(row.trades || []);
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    setEditWorking(true);
    try {
      await updateParticipant(projectId, editRow._id, {
        role: editRole,
        trades: editTrades,
      });
      message.success("Participant updated");
      setEditRow(null);
      load();
    } catch (e) {
      if (e.status === 403) handleOwnerOnly();
      else message.error(e.message || "Failed to update participant");
    } finally {
      setEditWorking(false);
    }
  };

  const openEditSupervisor = (row, idx) => {
    const sv = (row.supervisors || [])[idx] || {};
    setPersonEdit({ row, list: "supervisors", idx, initial: { name: sv.name || "", email: sv.email || "", kind: sv.isExecutive ? "executive" : "supervisor", title: "", showCost: !!sv.showCost } });
  };

  const handleSupervisorRemove = async (row, idx) => {
    try {
      await updateParticipant(projectId, row._id, {
        supervisors: (row.supervisors || []).filter((_, i) => i !== idx),
      });
      message.success("Supervisor removed");
      load();
    } catch (e) {
      if (e.status === 403) handleOwnerOnly();
      else message.error(e.message || "Failed to remove supervisor");
    }
  };

  const confirmRemove = (row) => {
    Modal.confirm({
      title: "Remove partner company?",
      content: `${companyName(row)} will no longer be a participant on this project.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeParticipant(projectId, row._id);
          message.success("Participant removed");
          load();
        } catch (e) {
          if (e.status === 403) handleOwnerOnly();
          else message.error(e.message || "Failed to remove participant");
        }
      },
    });
  };

  // A partner person's job title (T44): a select for that company's own
  // admin (canSetJobTitle from the server), a plain tag for everyone else.
  // A label only - it changes no access and no email.
  const jobTitleControl = (row, person) => {
    if (row.canSetJobTitle && jobTitles.length) {
      return (
        <Select
          size="small"
          allowClear
          placeholder="Job title"
          style={{ width: 130, marginLeft: 6 }}
          value={person.jobTitle || undefined}
          options={jobTitles.map((t) => ({ value: t, label: t }))}
          onChange={(v) => setPartnerJobTitle(projectId, row._id, person.email, v || "")
            .then(() => load())
            .catch((e) => message.error(e.message || "Could not save the job title"))}
        />
      );
    }
    return person.jobTitle
      ? <Tag style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>{person.jobTitle}</Tag>
      : null;
  };

  const columns = [
    {
      title: "Company",
      key: "company",
      render: (_, row) => (
        <>
          <span style={{ fontWeight: 500 }}>{companyName(row)}</span>
          <PartnerProfileSummary row={row} canEdit={canEdit} onFill={setProfileRow} />
        </>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 140,
      render: (v) => (v ? <Tag color={v === "owner" ? "green" : "blue"}>{v}</Tag> : "-"),
    },
    {
      title: "Trades",
      dataIndex: "trades",
      render: (trades) =>
        trades && trades.length > 0
          // The trade's NAME, not its key (owner, 2026-10-16).
          ? trades.map((t) => <Tag key={t}>{tradeNames([t], tradeOptions)[0]}</Tag>)
          : "-",
    },
    {
      // The heading names all three, because a roster that says
      // "supervisors and employees" while showing an Executive tag reads
      // as a bug to the person who just added one.
      title: "Supervisors, executives and employees",
      dataIndex: "supervisors",
      render: (supervisors, row) => (
        <div style={{ fontSize: 12 }}>
          {(supervisors || []).map((s, i) => (
            <div key={`s${i}`} style={{ whiteSpace: "nowrap" }}>
              {/* A PEER, NOT A VARIANT. An executive shown with the
                  supervisor tag would read as a supervisor to anyone
                  scanning the roster, and the whole point of the flag is
                  that he is NOT one - he is a reader. Its own tag, its
                  own colour, and the tooltip says what the difference
                  actually is rather than restating the label. */}
              {s.isExecutive ? (
                <Tooltip title="Receives one weekly summary of every project they are on. Not offered as a responsible person and never assigned work.">
                  <Tag color="purple" style={{ marginRight: 6, fontSize: 10, lineHeight: "16px" }}>Executive</Tag>
                </Tooltip>
              ) : (
                <Tag color="geekblue" style={{ marginRight: 6, fontSize: 10, lineHeight: "16px" }}>Supervisor</Tag>
              )}
              {s.showCost && (
                <Tooltip title="Sees rates, prices per hour and totals in currency in their digests and reports.">
                  <Tag color="gold" style={{ marginRight: 6, fontSize: 10, lineHeight: "16px" }}>Cost view</Tag>
                </Tooltip>
              )}
              <b>{s.name}</b>
              {s.email ? <span style={{ color: "#888" }}> - {s.email}</span> : <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>Needs an email</Tag>}
              {s.email ? jobTitleControl(row, s) : null}
              {canEdit && (
                <>
                  <Button type="text" size="small" icon={<EditOutlined />} title="Edit name / email" onClick={() => openEditSupervisor(row, i)} />
                  {row.role !== "owner" && s.email && linkButtons(row, s)}
                  {/* A supervisor has no account and nothing assigned, so this
                      moves the roster entry and nothing else. */}
                  {canMovePeople && s.email && (
                    <Button type="text" size="small" icon={<SwapOutlined />}
                            title="Move this supervisor to another contractor"
                            onClick={() => openMoveUser(row, { ...s, kind: "supervisor" })} />
                  )}
                  <Button type="text" danger size="small" onClick={() => handleSupervisorRemove(row, i)}>×</Button>
                </>
              )}
            </div>
          ))}
          {(row.users || []).map((u, i) => (
            <div key={`u${i}`} style={{ whiteSpace: "nowrap" }}>
              <Tag style={{ marginRight: 6, fontSize: 10, lineHeight: "16px" }}>{u.role || "Employee"}</Tag>
              <b>{u.name}</b>
              {u.email ? <span style={{ color: "#888" }}> - {u.email}</span> : <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>Needs an email</Tag>}
              {u.email ? jobTitleControl(row, u) : null}
              {u.invited && !u.userId && (
                <Tag color="orange" style={{ marginLeft: 6 }}>invited</Tag>
              )}
              {canEdit && (
                <>
                  <Button type="text" size="small" icon={<EditOutlined />} title="Edit name / email / role" onClick={() => openEditEmployee(row, i)} />
                  {row.role !== "owner" && u.email && linkButtons(row, u)}
                  {/* Admin-only, because it re-files the person's ACCOUNT -
                      which is what the responsible dropdowns group by - and
                      that follows them to every project their new company
                      is on. Ordinary participant editing does not reach
                      that far. */}
                  {canMovePeople && u.email && (
                    <Button type="text" size="small" icon={<SwapOutlined />}
                            title="Move this person to another contractor"
                            onClick={() => openMoveUser(row, u)} />
                  )}
                  <Button type="text" danger size="small" onClick={() => handleEmployeeRemove(row, i)}>×</Button>
                </>
              )}
            </div>
          ))}
          {(!supervisors || supervisors.length === 0) && (!row.users || row.users.length === 0) && !canEdit && "-"}
          {canEdit && row.role !== "owner" && (!supervisors || supervisors.length === 0) && (!row.users || row.users.length === 0) && (
            <div style={{ color: "#B45309", margin: "2px 0 4px" }}>
              Nobody from {companyName(row)} can take part yet - add a supervisor or employee by name and email.
            </div>
          )}
          {canEdit && (
            <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
              <Button size="small" onClick={() => openAddPerson(row)}>+ Add person</Button>
              {row.role !== "owner" && (
                <Button size="small" onClick={() => setCoreRow(row)}>Add from {companyName(row)}&apos;s people</Button>
              )}
              {(row.users || []).some((u) => !u.userId && u.email) && (
                <Button size="small" onClick={() => inviteUninvited(row)}>
                  Invite {(row.users || []).filter((u) => !u.userId && u.email).length} with no account
                </Button>
              )}
            </div>
          )}
          {canEdit && row.role !== "owner" && (
            <div style={{ marginTop: 6 }}>
              <Tooltip title="Off: you maintain this company's roster. On: their supervisors can add their own crew - the accounts stay limited to coordination either way.">
                <span style={{ color: "#888", marginRight: 8 }}>Crew self-serve</span>
              </Tooltip>
              <Switch
                size="small"
                checked={!!row.allowSupervisorInvite}
                onChange={(on) => toggleCrewInvite(row, on)}
              />
            </div>
          )}
          {!canEdit && row.canInviteCrew && (
            <div style={{ marginTop: 6 }}>
              <Button size="small" type="primary" ghost onClick={() => openAddCrew(row)}>
                + Add crew
              </Button>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Color",
      dataIndex: "color",
      width: 80,
      render: (color) =>
        color ? (
          <span
            style={{
              display: "inline-block",
              width: 18,
              height: 18,
              borderRadius: 4,
              background: color,
              border: "1px solid #d9d9d9",
              verticalAlign: "middle",
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (v) =>
        v ? <Tag color={v === "active" ? "green" : "default"}>{v}</Tag> : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, row) =>
        (canEdit || canDelete || row.canSeeRates || row.coordAccess) ? (
          <>
            {row.coordAccess && (
              <Tooltip title="Coordination access - which tabs this partner sees on this project, and what they may do there">
                <Button type="text" icon={<AppstoreOutlined />} aria-label={`Coordination access for ${row.companyName}`}
                        onClick={() => setCoordRow(row)} />
              </Tooltip>
            )}
            {row.canSeeRates && (
              <Tooltip title={row.hourlyRate != null ? `Hourly rates (company: $${row.hourlyRate}/h)` : "Hourly rates - cost of hours spent on issues"}>
                <Button type="text" icon={<DollarOutlined />} onClick={() => setRatesRow(row)} />
              </Tooltip>
            )}
            {canEdit && (
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              />
            )}
            {canDelete && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => confirmRemove(row)}
              />
            )}
          </>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif", maxWidth: 1000, margin: "0 auto" }}>
      {embedded ? null : (
        <CoordinationBreadcrumbs
          items={[{ label: "Dashboard & Schedule", to: `/coordination/${projectId}` }]}
          current="Partners"
        />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          {embedded ? null : <h2 style={{ margin: 0 }}>Project Participants</h2>}
          <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Partner companies working on this project, their roles and trades.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {exportCost && exportCost.mayChange && (
            <Tooltip title={exportCost.include
              ? "Issue exports (PDF and Excel) open with the hours spent, what they cost and the hourly rates behind them. Only company administrators ever see the cost on an export."
              : "Issue exports on this project show the hours spent without any cost or rates. Turn this on to include them."}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 4 }}>
                <Switch
                  size="small"
                  checked={!!exportCost.include}
                  loading={exportCostSaving}
                  onChange={toggleExportCost}
                />
                <span style={{ fontSize: 13, color: "#667085" }}>Cost in issue exports</span>
              </span>
            </Tooltip>
          )}
          {canEditPlan && (
            <Button
              icon={<ScheduleOutlined />}
              onClick={() => history.push(`/scheduler/${projectId}`)}
            >
              Schedule
            </Button>
          )}
          {canAdd && (
            <Button onClick={openCopy}>Copy from another project</Button>
          )}
          {/* Import partner documentation (owner, 2026-10-15): gated on
              participantAdd like "Add partner company" - an import is many
              adds, reviewed in the Review Center before any of them happens. */}
          {canAdd && (
            <PartnerImportButton projectId={projectId} onUploaded={() => setImportTick((t) => t + 1)} />
          )}
          {canAdd && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddOpen(true)}
            >
              Add partner company
            </Button>
          )}
          {!canManage && (
            <Tag style={{ alignSelf: "center" }}>View only</Tag>
          )}
        </div>
      </div>

      <ParticipantRatesModal
        open={!!ratesRow}
        projectId={projectId}
        row={ratesRow}
        onClose={() => setRatesRow(null)}
        onSaved={() => load()}
      />

      <Modal
        open={!!coordRow}
        title={coordRow ? `Coordination access - ${coordRow.companyName}` : ""}
        onCancel={() => setCoordRow(null)}
        footer={[<Button key="c" onClick={() => setCoordRow(null)}>Close</Button>]}
        width={680}
        destroyOnClose
      >
        {coordRow ? (
          <CoordinationAccessGrid
            participantId={coordRow._id}
            initial={coordRow.coordAccess}
            canEdit={!!coordRow.canSetCoordGrants}
            onSaved={() => load()}
          />
        ) : null}
      </Modal>

      <PartnerImportStatus projectId={projectId} reloadKey={importTick} canRetry={canAdd} />

      <OwnerTeamCard projectId={projectId} onJobTitles={setJobTitles} />

      <PartnerMissingBanner participants={participants} onAddEmails={canEdit ? () => setEmailsOpen(true) : null} />
      <PartnerEmailsDialog
        open={emailsOpen}
        participants={participants}
        projectId={projectId}
        onClose={() => setEmailsOpen(false)}
        onSaved={() => { setEmailsOpen(false); load(); }}
      />

      <PartnerCorePicker projectId={projectId} row={coreRow} onClose={() => setCoreRow(null)} onAdded={() => load()} />

      <PartnerProfileEditor
        open={!!profileRow}
        row={profileRow}
        projectId={projectId}
        tradeOptions={tradeOptions}
        onClose={() => setProfileRow(null)}
        onSaved={() => { setProfileRow(null); load(); }}
      />

      <Table
        loading={loading}
        dataSource={participants}
        columns={columns}
        rowKey={(r) => r._id}
        pagination={false}
        locale={{ emptyText: <Empty description="No participants yet" /> }}
      />

      {/* Oversight entry, read-only and locked (owner brief 2026-10-11 T40, T42). */}
      <OversightPanel projectId={projectId} />

      {/* ─────────── in no company: the second list, and the safety valve ───
          The automatic half of this feature files a recipient into a partner
          company by email domain, and only when that domain points at exactly
          ONE company. Every refusal - a shared mailbox, a domain typed into
          two contractors' rosters, a firm nobody has entered yet - lands here
          instead of nowhere. Without this section the refusals are the
          invisible outcome, and a project where nobody could be placed looks
          identical to one where everybody already is. */}
      {unplacedError ? (
        <div style={{ marginTop: 20, fontSize: 12.5, color: "#8a6d3b", background: "#FFF7ED", border: "1px solid #FDBA74", borderRadius: 6, padding: "8px 10px" }}>
          Could not work out who is in no company — {unplacedError}. The list above is
          unaffected; nobody has been placed or unplaced by this.
        </div>
      ) : null}

      {unplaced ? (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0 }}>
              In no company{(unplaced.withoutCompany || []).length ? ` (${unplaced.withoutCompany.length})` : ""}
            </h3>
            {/* Counted by the server in the same pass as the list, so the two
                halves of the owner's question cannot disagree with each other. */}
            <span style={{ color: "#888", fontSize: 12.5 }}>
              {unplaced.placedCount} placed across {unplaced.companyCount}
              {unplaced.companyCount === 1 ? " company" : " companies"}
            </span>
          </div>
          <div style={{ color: "#888", fontSize: 13, margin: "4px 0 12px" }}>
            People this project emails a finished deliverable to who are on no
            contractor's roster. Anybody whose email domain belongs to exactly one
            company here is filed there for you; everybody else waits here, because
            a guess sends one firm's drawings to another.
          </div>

          {(unplaced.withoutCompany || []).length === 0 ? (
            <Empty description="Everybody this project emails is on a company roster" />
          ) : (
            (unplaced.withoutCompany || []).map((p) => {
              const tag = UNPLACED_TAG[p.state] || UNPLACED_TAG["no-account"];
              return (
                <div key={p.email} style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <b>{p.name || p.email}</b>
                      {p.name ? <span style={{ color: "#888" }}> — {p.email}</span> : null}
                      <Tag color={tag.color} style={{ marginLeft: 8 }}>{tag.label}</Tag>
                    </div>
                    {canEdit && (
                      <Select
                        size="small"
                        style={{ minWidth: 210 }}
                        value={undefined}
                        placeholder="Place with a contractor…"
                        options={(participants || [])
                          .filter((r) => r.role !== "owner")
                          .map((r) => ({ value: r._id, label: <PartnerWithTrades name={companyName(r)} trades={tradeNames(r.trades, tradeOptions)} /> }))}
                        // Opens the ordinary add-employee dialog, prefilled.
                        // The role is left for a human because it is the one
                        // thing no rule here knows, and the placement itself
                        // goes through the same route as every other roster
                        // edit. When it saves, the reload drops this person
                        // from the list - they are on a roster now.
                        onSelect={(pid) => {
                          const row = (participants || []).find((r) => String(r._id) === String(pid));
                          if (row) openAddEmployee(row, { name: p.name, email: p.email });
                        }}
                      />
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 4 }}>{p.reason}</div>
                  {p.hint ? <div style={{ fontSize: 12.5, color: "#6B7280" }}>{p.hint}</div> : null}
                  {(p.drawings || []).length ? (
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>
                      Named on {p.drawings.join(", ")}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}

          {/* THE CONFIRMING HALF, and the reason it is here rather than
              implied by the table above: this is where the automatic
              placement can be SEEN working. A recipient whose domain
              belonged to exactly one company was filed by a rule nobody
              watched, so the list that shows who it filed, and where, is
              the only way that rule is ever checked. Compact on purpose -
              it is read when somebody is surprised, not every day. */}
          {(unplaced.withCompany || []).length ? (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                On a company roster ({unplaced.withCompany.length})
              </div>
              <div style={{ fontSize: 12.5, color: "#6B7280" }}>
                {unplaced.withCompany.map((p) => (
                  <div key={p.email} style={{ padding: "2px 0" }}>
                    {p.name || p.email}
                    {p.name ? <span style={{ color: "#9CA3AF" }}> — {p.email}</span> : null}
                    <span style={{ color: "#374151" }}> · {p.companyName || "a company on this project"}</span>
                    {p.hasAccount ? null : <Tag style={{ marginLeft: 6 }}>no account yet</Tag>}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Add partner modal */}
      <Modal
        title="Add partner company"
        open={addOpen}
        okText="Add"
        confirmLoading={addWorking}
        onOk={handleAdd}
        onCancel={() => setAddOpen(false)}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13 }}>
            Your partners
            <span style={{ color: "#888", marginLeft: 6, fontWeight: 400 }}>
              already working with your company - pick which of their people work here next
            </span>
          </div>
          <Select filterOption={matchOption}
            allowClear
            showSearch
            optionFilterProp="search"
            optionLabelProp="text"
            style={{ width: "100%" }}
            loading={partnerOrgs.loading}
            placeholder={partnerOrgs.orgs.length ? "Pick a partner organisation" : "No partners on your other projects yet"}
            value={pickedOrg ? addCompanyId : undefined}
            onChange={(v) => {
              setAddCompanyId(v);
              const o = partnerOrgs.orgs.find((x) => x.companyId === v);
              if (o) { setAddRole(o.role); setAddTrades(o.trades || []); }
            }}
            options={partnerOrgs.orgs.map((o) => {
              const text = `${o.name} - ${o.people} ${o.people === 1 ? "person" : "people"}, on ${o.projects} project${o.projects === 1 ? "" : "s"}${o.onThisProject ? " (already here)" : ""}`;
              const t = tradeNames(o.trades, tradeOptions);
              return { value: o.companyId, disabled: o.onThisProject, text, search: `${text} ${t.join(" ")}`, label: <PartnerWithTrades name={text} trades={t} /> };
            })}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13 }}>
            {partnerOrgs.orgs.length ? "Or a new company" : "New company"}
            <span style={{ color: "#888", marginLeft: 6, fontWeight: 400 }}>
              its name - the firm is recognised from the first contact's email
            </span>
          </div>
          <Input
            placeholder="Company name"
            value={pickedOrg ? "" : newCompanyName}
            disabled={!!pickedOrg}
            onChange={(e) => { setNewCompanyName(e.target.value); if (pickedOrg) setAddCompanyId(undefined); }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13 }}>Role</div>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder="Role (optional)"
            value={addRole}
            onChange={setAddRole}
            options={ROLE_OPTIONS}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13 }}>Trades</div>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder={tradeOptions.length ? "Pick trades (or type a custom one)" : "Add trades (optional)"}
            value={addTrades}
            onChange={setAddTrades}
            options={tradeOptions}
            optionFilterProp="label"
            tokenSeparators={[","]}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13 }}>
            Contact users {pickedOrg && pickedOrg.people ? <span style={{ color: "#888" }}>(optional)</span> : <span style={{ color: "#c00" }}>*</span>}
            <span style={{ color: "#888", marginLeft: 6, fontWeight: 400 }}>
              {pickedOrg && !pickedOrg.people
                ? `${pickedOrg.name} has nobody on your other projects yet - add at least one contact (unknown emails get a registration invite)`
                : pickedOrg
                ? `its ${pickedOrg.people} ${pickedOrg.people === 1 ? "person comes" : "people come"} from your other projects, not invited - add anyone new here (unknown emails get a registration invite)`
                : "name, email and role required; unknown emails get a registration invite"}
            </span>
          </div>
          {addUsers.map((u) => (
            <div key={u.key} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <Input placeholder="Full name" value={u.name} style={{ flex: 1 }}
                     onChange={(e) => setContact(setAddUsers)(u.key, { name: e.target.value })} />
              <Input placeholder="Email" value={u.email} style={{ flex: 1 }}
                     onChange={(e) => setContact(setAddUsers)(u.key, { email: e.target.value })} />
              <Input placeholder="Role (e.g. PM)" value={u.role} style={{ width: 130 }}
                     onChange={(e) => setContact(setAddUsers)(u.key, { role: e.target.value })} />
              <Button type="text" danger disabled={addUsers.length === 1}
                      onClick={() => setAddUsers((us) => us.filter((x) => x.key !== u.key))}>
                ×
              </Button>
            </div>
          ))}
          <Button size="small" onClick={() => setAddUsers((us) => [...us, emptyContact()])}>
            + Add contact
          </Button>
        </div>
      </Modal>

      {/* Edit role/trades modal */}
      <Modal
        title={editRow ? `Edit — ${companyName(editRow)}` : "Edit participant"}
        open={!!editRow}
        okText="Save"
        confirmLoading={editWorking}
        onOk={handleEditSave}
        onCancel={() => setEditRow(null)}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13 }}>Role</div>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder="Role"
            value={editRole}
            onChange={setEditRole}
            options={ROLE_OPTIONS}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13 }}>Trades</div>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder={tradeOptions.length ? "Pick trades (or type a custom one)" : "Trades"}
            value={editTrades}
            onChange={setEditTrades}
            options={tradeOptions}
            optionFilterProp="label"
            tokenSeparators={[","]}
          />
        </div>
      </Modal>

      {/* ---- Copy partners from another project ---------------------------
          IT SAYS WHAT IT WILL CARRY BEFORE HE CONFIRMS. He ruled "copy
          everything exactly as it is" knowingly, and the dialog should
          still SHOW the money and the permission rather than moving them
          silently - same rule as the bulk-status confirmation naming its
          number. Rates appear here only if the server sent them; it strips
          them from anyone who may not see them, so the permission is
          enforced on the READ side and this cannot leak one. */}
      <Modal
        title="Copy partners from another project"
        open={copyOpen}
        okText={copyPicked.length ? `Copy ${copyPicked.length} partner${copyPicked.length === 1 ? "" : "s"}` : "Copy"}
        okButtonProps={{ disabled: !copyPicked.length }}
        confirmLoading={copyBusy}
        onOk={runCopy}
        onCancel={() => setCopyOpen(false)}
        width={640}
      >
        <div style={{ color: "#888", fontSize: 13, marginBottom: 10 }}>
          People, rates and settings come across as they are on the other
          project (plan permissions only when a company admin copies). Nobody is
          emailed - you choose who to invite afterwards. A partner already on this
          project is not copied again; its people are synced instead, as they are
          on every project of your company.
        </div>
        <Select
          style={{ width: "100%" }}
          placeholder="Which project?"
          value={copyFrom}
          onChange={chooseCopySource}
          options={(copySources || []).map((p) => ({
            value: p._id,
            label: `${p.title} - ${p.partners} partner${p.partners === 1 ? "" : "s"}`,
          }))}
        />
        {copyFrom && (
          <div style={{ marginTop: 12, maxHeight: 320, overflow: "auto" }}>
            {copyRows.length === 0 && <Empty description="No partners on that project" />}
            {copyRows.map((r) => {
              const already = participants.some(
                (p) => String(p.companyId) === String(r.companyId) && p.status !== "removed"
              );
              const people = (r.users || []).length + (r.supervisors || []).length;
              const rates = (r.hourlyRate != null)
                || (r.users || []).some((u) => u.hourlyRate != null)
                || (r.supervisors || []).some((x) => x.hourlyRate != null);
              const execs = (r.supervisors || []).filter((x) => x.isExecutive).length;
              return (
                <div key={r._id} style={{
                  display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0",
                  opacity: already ? 0.45 : 1,
                }}>
                  <input
                    type="checkbox"
                    disabled={already}
                    checked={copyPicked.includes(String(r.companyId))}
                    onChange={(e) => setCopyPicked((prev) => (e.target.checked
                      ? [...prev, String(r.companyId)]
                      : prev.filter((c) => c !== String(r.companyId))))}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ fontSize: 12 }}>
                    <b>{r.companyName}</b>
                    {already && <Tag style={{ marginLeft: 6 }}>already on this project</Tag>}
                    <div style={{ color: "#888" }}>
                      {people} {people === 1 ? "person" : "people"}
                      {execs > 0 && ` - ${execs} executive${execs === 1 ? "" : "s"}`}
                      {rates && " - rates included"}
                      {r.allowSupervisorInvite && " - supervisors can create accounts"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* THE ONE ADD / EDIT PERSON DIALOG, shared with the all-projects view. */}
      <PartnerPersonModal
        open={!!personEdit}
        title={personEdit ? `${personEdit.list ? "Edit" : "Add"} person - ${companyName(personEdit.row)}` : "Add person"}
        okText={personEdit && personEdit.list ? "Save" : "Add"}
        busy={personWorking}
        initial={personEdit ? personEdit.initial : null}
        canSetCostView={!!(personEdit && personEdit.row.canSetCostView)}
        onCancel={() => setPersonEdit(null)}
        onSave={savePerson}
        intro="Supervisors receive the weekly coordination report for this company. People (with a title) appear in the responsible dropdowns for issues and schedule activities. If they have no BidLight account yet they receive an invite; assigning them later signs them in through their email link."
      />

      {/* THE ONE ACCESS DIALOG, scoped to this roster row. Mounted only while
          somebody is being looked at, and keyed on row and address. */}
      {access ? (
        <PartnerAccessDialog
          key={`${access.row._id}:${access.person.email}`}
          email={access.person.email}
          name={access.person.name}
          scope={{ projectId, rowId: access.row._id, companyName: access.row.companyName }}
          onClose={() => setAccess(null)}
        />
      ) : null}

      <Modal
        title={crewRow ? `Add crew - ${companyName(crewRow)}` : "Add crew"}
        open={!!crewRow}
        okText="Add"
        confirmLoading={crewWorking}
        onOk={handleCrewAdd}
        onCancel={() => setCrewRow(null)}
      >
        <div style={{ color: "#888", fontSize: 13, marginBottom: 12 }}>
          They join your company on this project and can view and update the
          issues assigned to them, report progress and propose resolutions.
          They cannot invite anyone else. Their first issue email signs them in.
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Input placeholder="Full name" value={crewName} style={{ flex: 1 }}
                 onChange={(e) => setCrewName(e.target.value)} />
          <Input placeholder="Email" value={crewEmail} style={{ flex: 1 }}
                 onChange={(e) => setCrewEmail(e.target.value)} />
          <Input placeholder="Role (e.g. Pipefitter)" value={crewRole} style={{ flex: 1 }}
                 onChange={(e) => setCrewRole(e.target.value)} />
        </div>
      </Modal>

      {/* Move a person or supervisor to another contractor. Admins, company admins and the project owner — see canMovePeople. */}
      <Modal
        open={!!movePerson}
        title={`Move ${movePerson?.name || "this person"} to another contractor`}
        onCancel={() => { if (!moveWorking) { setMoveRow(null); setMovePerson(null); } }}
        onOk={submitMoveUser}
        okText="Move"
        okButtonProps={{ loading: moveWorking, disabled: !moveTo }}
        destroyOnClose
      >
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          <b>{movePerson?.name}</b>
          <span style={{ color: "#888" }}> — {movePerson?.email}</span>
          <div style={{ color: "#888", marginTop: 4 }}>
            Currently under <b>{moveRow?.companyName || "this contractor"}</b>.
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>Move to</div>
          <Select filterOption={matchOption}
            style={{ width: "100%" }} showSearch optionFilterProp="search" optionLabelProp="text"
            placeholder="Contractor on this project"
            value={moveTo} onChange={setMoveTo}
            options={(participants || [])
              .filter((r) => String(r._id) !== String(moveRow?._id) && r.companyId && r.role !== "owner")
              .map((r) => {
                const name = r.companyName || String(r.companyId);
                const t = tradeNames(r.trades, tradeOptions);
                return { value: String(r.companyId), text: name, search: `${name} ${t.join(" ")}`, label: <PartnerWithTrades name={name} trades={t} /> };
              })}
          />
        </div>
        {movePerson?.kind === "supervisor" ? (
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
            A supervisor receives the project-wide report for their contractor and holds no account
            and no assignments, so only the roster entry moves.
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Switch size="small" checked={moveAssignments} onChange={setMoveAssignments} />
            <span style={{ fontSize: 12.5 }}>
              Move what they are responsible for as well (checklist items, plan items, issues)
            </span>
          </div>
        )}
        <div style={{ fontSize: 12, color: "#8a6d3b", background: "#FFF7ED", border: "1px solid #FDBA74", borderRadius: 6, padding: "8px 10px" }}>
          This re-files their BidLight account, which is what every responsible
          dropdown is grouped by, so it follows them to every project the new
          contractor is on — not just this one. A row shared with other people
          from the old contractor is left alone and reported rather than moved.
        </div>
      </Modal>
    </div>
  );
}
